/* eslint-disable max-lines */
import { SOFTWARE_QUALITY } from '@/lib/constants/appsec/sast'

export const APPSEC = {
  MUTE: {
    MUTE_BUTTON: 'Silenciar finding',
    UNMUTE_BUTTON: 'Reactivar finding',
    DIALOG_TITLE: 'Motivo del silenciado',
    SELECTION_SUMMARY: (selected: number, total: number) => `${selected} de ${total} findings seleccionados`,
    JUSTIFICATION_LABEL: 'Justificación',
    JUSTIFICATION_PLACEHOLDER:
      '¿Por qué se silencia este finding? p. ej. falso positivo en código generado, aceptado hasta la próxima release',
    CANCEL: 'Cancelar',
    BANNER_TITLE: (muted: number, total: number) => `Silenciado · ${muted} de ${total} findings`,
    MUTED_BADGE: 'Silenciado',
    MUTED_BY: (name: string) => `Silenciado por ${name}`,
    BY_SCANNER: 'Silenciado por el escáner',
    NOTHING_SELECTED_TOOLTIP: 'Selecciona al menos un finding',
    DORMANT_NOTE: 'Este silenciado volverá a aplicarse si el finding aparece en un análisis futuro',
    MUTE_SUCCESS: 'Finding silenciado',
    UNMUTE_SUCCESS: 'Finding reactivado',
    MUTE_PARTIAL: (muted: number, requested: number) =>
      `Silenciados ${muted} de ${requested} findings — el resto ya no estaban abiertos`,
    UNMUTE_PARTIAL: (unmuted: number, requested: number) =>
      `Reactivados ${unmuted} de ${requested} findings — el resto ya no estaban silenciados`,
    MUTE_ERROR: 'No se ha podido silenciar el finding',
    UNMUTE_ERROR: 'No se ha podido reactivar el finding',
  },
  COMMON: {
    CVSS_SCORE: 'Puntuación CVSS',
    CWE: 'CWE',
    CVE: 'CVE',
    TIME_TO_RESOLVE: {
      RESOLVED: 'Resuelta',
      RESOLVED_IN: (duration: string) => `Resuelta en ${duration}`,
      FIRST_SEEN: 'Detectada',
      CLOSED: 'Cerrada',
      DURATION: 'Tiempo de resolución',
      DAYS: (days: number) => (days === 1 ? '1 día' : `${days} días`),
      LESS_THAN_A_DAY: 'menos de un día',
    },
    ITEM: {
      DEPENDENCIES: 'dependencias',
      CONTAINERS: 'contenedores',
      SERVICES: 'servicios',
      REPOSITORIES: 'repositorios',
      FILES: 'archivos',
    },
    DETAILS: {
      DESCRIPTION: 'Descripción',
      REMEDIATION: 'Remediación',
      SUMMARY: 'Resumen',
      PACKAGES: 'Paquetes',
    },
    EXPORT: {
      BUTTON: 'Exportar CSV',
      EMPTY: 'No hay hallazgos que exportar.',
      STATUS: 'Estado',
      AUTHOR: 'Autor',
      MUTED_REASON: 'Motivo del silenciado',
      MUTED_BY: 'Silenciado por',
      MUTED_WHEN: 'Silenciado el',
    },
  },
  DASHBOARD: {
    TITLE: 'Panel de Seguridad de Aplicaciones',
    GENERAL_METRICS: {
      IMAGES: 'Contenedores Analizados',
      WEBAPPS: 'Aplicaciones Web Analizadas',
      REPOSITORIES: 'Repositorios Analizados',
    },
    TOP_FIVE: {
      CONTAINERS: 'Contenedores de Mayor Riesgo',
      WEBAPPS: 'Aplicaciones Web de Mayor Riesgo',
      REPOSITORIES: 'Repositorios de Mayor Riesgo',
      RISK_SCORE: 'Puntuación de riesgo',
      NO_DATA: 'No hay datos disponibles',
    },
    FINDINGS_OVERVIEW: {
      TOTAL_FINDINGS: 'Hallazgos Actuales',
      CRITICAL: 'Crítico',
      HIGH: 'Alto',
      MEDIUM: 'Medio',
      LOW: 'Bajo',
      TITLE: 'Resumen de Hallazgos',
      NO_DATA: 'No hay datos disponibles',
    },
    TRENDS: {
      sast: 'SAST',
      sca: 'SCA',
      secrets: 'Secretos',
      dast: 'DAST',
      containers: 'Contenedores',
    },
    TRENDS_TITLE: (trend: string) => `Tendencia de ${trend}`,
    TRENDS_COMPUTING: 'Calculando tendencias',
    FINDINGS_BY_TYPE: 'Hallazgos por Tipo',
    LAST_SCAN: 'Último escaneo',
  },
  SAST: {
    TITLE: 'Análisis Estático de Seguridad de Aplicaciones',
    SUBTITLE: 'Problemas de seguridad detectados mediante análisis estático de código',
    OVERVIEW: {
      BY_TYPE: 'por Tipo',
    },
    SOFTWARE_QUALITY: {
      [SOFTWARE_QUALITY.SECURITY]: 'Seguridad',
      [SOFTWARE_QUALITY.RELIABILITY]: 'Fiabilidad',
      [SOFTWARE_QUALITY.MAINTAINABILITY]: 'Mantenibilidad',
      [SOFTWARE_QUALITY.READABILITY]: 'Legibilidad',
    },
    TABLE: {
      NAME: 'Nombre',
      SOFTWARE_QUALITY: 'Tipo',
      SEVERITY: 'Severidad',
      ISSUES: 'Problemas',
      FILES: 'Archivos',
      REPOSITORY: 'Repositorio',
      REPOSITORIES: 'Repositorios',
    },
    STATUS: {
      OPEN: 'Fallido',
      CLOSED: 'Pasado',
      MUTED: 'Silenciado',
    },
    DETAILS: {
      ISSUES: 'Problemas',
      DESCRIPTION: 'Descripción',
      REMEDIATION: 'Remediación',
      AFFECTED_FILES: 'Archivos Afectados',
      AUTHOR: 'Autor:',
      FILE_PATH: 'Ruta del Archivo',
      LINE: 'Línea',
      CODE_SNIPPET: 'Fragmento de Código',
      NEW: 'Nuevo',
      REPOSITORIES: 'Repositorios',
    },
    PAGINATION: {
      SHOWING: 'Mostrando',
      OF: 'de',
      PREVIOUS: 'Anterior',
      NEXT: 'Siguiente',
    },
  },
  SCA: {
    TITLE: 'Análisis de Composición de Software',
    SUBTITLE: 'Vulnerabilidades detectadas en librerías y dependencias de terceros',
    TABS: {
      VULNERABILITIES: 'Vulnerabilidades',
      DEPENDENCIES: 'Inventario SBOM',
    },
    OVERVIEW: {
      SEVERITY: 'Severidad',
      NO_ISSUES: 'Sin problemas',
      TOP_PACKAGES: 'Top Paquetes',
    },
    TABLE: {
      CVE: 'CVE',
      VULNERABILITY_DESCRIPTION: 'Descripción de Vulnerabilidad',
      SEVERITY: 'Severidad',
      PACKAGE_NAME: 'Paquete',
      PACKAGE_VERSION: 'Versión',
      REPOSITORY: 'Repositorio',
      STATUS: 'Estado',
      REPOSITORIES: 'Repositorios',
    },
    STATUS: {
      OPEN: 'Fallido',
      CLOSED: 'Pasado',
      MUTED: 'Silenciado',
    },
    DETAILS: {
      AFFECTED_PACKAGES: 'Paquetes afectados',
      DESCRIPTION: 'Descripción',
      REMEDIATION: 'Remediación',
    },
  },
  SECRETS_DETECTION: {
    TITLE: 'Detección de Secretos',
    SUBTITLE: 'Credenciales y claves API expuestas detectadas en el código',
    TABLE: {
      SECRET_CATEGORY: 'Categoría de Secreto',
      SEVERITY: 'Severidad',
      FILE_PATH: 'Archivo',
      LINE_NUMBER: 'Línea',
      SECRET_MATCH: 'Coincidencia',
      REPOSITORY_NAME: 'Repositorio',
      REPOSITORIES: 'Repositorios',
      STATUS: 'Estado',
    },
    STATUS: {
      OPEN: 'Fallido',
      CLOSED: 'Pasado',
      MUTED: 'Silenciado',
    },
  },
  SBOM: {
    TITLE: 'Lista de Materiales de Software',
    SUBTITLE: 'Inventario completo de todos los componentes y dependencias de software',
    SEARCH_PLACEHOLDER: 'Busca dependencias...',
    TABLE: {
      DEPENDENCY_NAME: 'Nombre de Dependencia',
      VERSION: 'Versión',
      LICENSE: 'Licencia',
      TYPE: 'Tipo',
    },
  },
  CONTAINERS_SCAN: {
    TITLE: 'Seguridad de Contenedores',
    SUBTITLE: 'Problemas de seguridad detectados en imágenes de contenedor',
    TABLE: {
      VULNERABILITY_NAME: 'Nombre de Vulnerabilidad',
      SEVERITY: 'Severidad',
      PACKAGE: 'Paquete',
      PACKAGE_AND_VERSION: 'Paquete:Version',
      CONTAINER: 'Contenedor',
      STATUS: 'Estado',
      VERSION: 'Versión',
      CONTAINERS: 'Contenedores',
    },
    STATUS: {
      OPEN: 'Fallido',
      CLOSED: 'Pasado',
      MUTED: 'Silenciado',
    },
    DETAILS: {
      DESCRIPTION: 'Descripción',
      REMEDIATION: 'Remediación',
      CVSS_SCORE: 'Puntuación CVSS',
      CVE: 'CVE',
      REFERENCES: 'Referencias',
      AFFECTED_CONTAINERS: 'Contenedores Afectados',
    },
  },
  DAST: {
    TITLE: 'Análisis Dinámico de Seguridad de Aplicaciones',
    SUBTITLE: 'Problemas de seguridad detectados en escaneos de sitios web en producción',
    OVERVIEW: {
      SEVERITY: 'Severidad',
      NO_ISSUES: 'Sin problemas',
      TOP_SUBDOMAINS: 'Top Subdominios',
      VULNERABILITIES: 'Vulnerabilities',
    },
    TABLE: {
      VULNERABILITY_NAME: 'Nombre',
      SEVERITY: 'Severidad',
      AFFECTED_SERVICES: 'Servicios',
      SUBDOMAIN: 'Subdominio',
      SUBDOMAINS: 'Subdominios',
      STATUS: 'Estado',
    },
    STATUS: {
      OPEN: 'Fallido',
      CLOSED: 'Pasado',
      MUTED: 'Silenciado',
    },
    DETAILS: {
      DESCRIPTION: 'Descripción',
      REMEDIATION: 'Remediación',
      CVSS_SCORE: 'Puntuación CVSS',
      CWE: 'CWE',
      REFERENCES: 'Referencias',
      AFFECTED_SERVICES: 'Servicios Afectados',
      SUBDOMAINS: 'Subdominios',
      HOST: 'Host',
      PATH: 'Ruta',
    },
    PAGINATION: {
      SHOWING: 'Mostrando',
      OF: 'de',
      PREVIOUS: 'Anterior',
      NEXT: 'Siguiente',
    },
  },
} as const
